import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './InvoiceList.css';
//import InvoiceForm from '../InvoiceFormx';
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Pagination, PaginationItem, PaginationLink, Popover, PopoverBody, Row, Spinner } from 'reactstrap';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaEdit, FaEllipsisH, FaFileInvoice, FaFilePdf, FaFilter, FaInfo, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { deliveryActions } from '../../sagas/deliveryNoteSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { DELIVERY_NOTE_PAGE } from '../../routes/routeConstants';
import PaymentBloc from './PaymentBloc';
import { format, parse } from 'date-fns';
import { invoiceSupplierActions } from '../../sagas/invoiceSupplierSlice';
import InvoiceForm from './InvoiceForm';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import "jspdf-autotable";
import autoTable from 'jspdf-autotable';

const SupplierInvoiceList = () => {
    const dispatch = useDispatch();
    const { invoices, loading } = useSelector((state) => state.invoiceSupplier);
    const { company } = useSelector(state => state.auth);
    const { t } = useTranslation();
    const searchInputRef = useRef(null);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const [filters, setFilters] = useState({
        fournisseurId: null,
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(tomorrowDate),
        query: ''
    });

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [invoices]);

    useEffect(() => {
        let payload = {
            ...filters
        };
        dispatch(invoiceSupplierActions.search(payload));
    }, [filters]);


    const debouncedSearch = useCallback(
        debounce((q) => {
            dispatch(invoiceSupplierActions.search({
                ...filters,
                query: q,
            }));
        }, 500), // Wait 500ms after last keystroke
        [filters.query]
    );



    const handleGeneratePdf = (invoice) => {
        // Create a wrapper function for convertNumberToWords to add the currency suffix
        const convertAmountToWords = (amount) => {
            const words = convertNumberToWords(Math.round(amount));
            return `${words} dirhams`;
        };

        // Set colors for supplier invoice document
        const primaryColor = [142, 68, 173]; // Purple for Supplier Invoice
        const secondaryColor = [155, 89, 182];
        const documentTitle = `FACTURE FOURNISSEUR N° ${invoice.numero}`;
        const documentType = 'facture-fournisseur';

        // Create PDF with slightly larger page format for better layout
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        // Set document properties
        doc.setProperties({
            title: documentTitle,
            subject: documentTitle,
            author: 'MatjarPOS',
            creator: 'MatjarPOS System'
        });

        // Get page dimensions
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 15;
        const footerHeight = 20;
        const headerHeight = 40;

        // Extract supplier information
        const supplierName = invoice.fournisseurNom;
        const supplierContact = [
            invoice.fournisseurTelephone,
            invoice.fournisseurEmail,
        ].filter(Boolean).join(", ");

        // Use actual company data from the Redux store if available
        const companyData = {
            name: company?.name || "Ma Société",
            subtitle: company?.subtitle || "Votre partenaire de confiance",
            address: company?.address || "123 Rue Exemple, Ville",
            phone1: company?.phone1 || "0123456789",
            phone2: company?.phone2 || "0987654321",
            email: company?.email || "contact@masociete.com",
            cnss: company?.cnss || "123456789",
            ice: company?.ice || "123456789",
            rc: company?.rc || "123456789",
            if: company?.identificationFiscale || "123456789",
            logo: company?.logo || null,
            website: company?.website || "www.masociete.com",
        };

        // Function to add header to each page
        const addHeader = (doc, isFirstPage = false) => {
            // Add modern header with gradient
            doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
            doc.rect(0, 0, pageWidth, isFirstPage ? headerHeight : 20, 'F');
            doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
            doc.rect(0, 0, pageWidth, 5, 'F');

            // Add logo if available and it's the first page
            if (isFirstPage && companyData.logo) {
                try {
                    doc.addImage(companyData.logo, 'JPEG', margin, 10, 40, 20);
                } catch (e) {
                    console.error("Error adding logo to PDF", e);
                }
            }

            // Add company name and subtitle with improved styling
            doc.setTextColor(255, 255, 255);

            if (isFirstPage) {
                doc.setFontSize(22);
                doc.setFont('helvetica', 'bold');
                doc.text(companyData.name, pageWidth / 2, 18, { align: 'center' });

                doc.setFontSize(12);
                doc.setFont('helvetica', 'normal');
                doc.text(companyData.subtitle, pageWidth / 2, 25, { align: 'center' });

                // Add company contact info in header
                doc.setFontSize(8);
                doc.text(`${companyData.address} | Tél: ${companyData.phone1} | Email: ${companyData.email}`, pageWidth / 2, 32, { align: 'center' });
            } else {
                // Simplified header for continuation pages
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text(`${documentTitle} (Suite)`, pageWidth / 2, 12, { align: 'center' });
            }
        };

        // Function to add footer to each page
        const addFooter = (doc) => {
            // Add footer with modern design
            doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
            doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'normal');
            doc.text(companyData.name, pageWidth / 2, pageHeight - 14, { align: 'center' });
            doc.setFontSize(7);
            doc.text(`${companyData.address} | Tél: ${companyData.phone1} | Email: ${companyData.email} | ICE: ${companyData.ice}`, pageWidth / 2, pageHeight - 9, { align: 'center' });
            doc.text(`RC: ${companyData.rc} | IF: ${companyData.if} | CNSS: ${companyData.cnss} | ${companyData.website}`, pageWidth / 2, pageHeight - 4, { align: 'center' });

            // Add page number
            doc.setFontSize(8);
            doc.setTextColor(255, 255, 255);
            doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
        };

        // Add first page header
        addHeader(doc, true);

        // Add document title with elegant styling
        doc.setFillColor(240, 240, 240);
        doc.roundedRect(margin, 45, pageWidth - (2 * margin), 12, 2, 2, 'F');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(documentTitle, pageWidth / 2, 53, { align: 'center' });

        // Format date with better styling
        const formattedDate = invoice.dateFacture || new Date().toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

        // Create the Supplier Information Card on the left
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(margin, 65, (pageWidth - (3 * margin)) / 2, 35, 2, 2, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(margin, 65, (pageWidth - (3 * margin)) / 2, 35, 2, 2, 'S');

        // Supplier Information Header
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.roundedRect(margin, 65, (pageWidth - (3 * margin)) / 2, 8, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text("FOURNISSEUR", margin + ((pageWidth - (3 * margin)) / 4), 70.5, { align: 'center' });

        // Supplier Information Content
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(supplierName, margin + 5, 78);
        doc.setFont('helvetica', 'normal');
        doc.text(`Adresse: ${invoice.fournisseurAdresse || "N/A"}`, margin + 5, 83);
        doc.text(`Contact: ${supplierContact || "N/A"}`, margin + 5, 88);
        doc.text(`ICE: ${invoice.fournisseurIce || "N/A"}`, margin + 5, 93);

        // Create the Company Information Card on the right
        const rightColumnX = margin * 2 + (pageWidth - (3 * margin)) / 2;
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(rightColumnX, 65, (pageWidth - (3 * margin)) / 2, 35, 2, 2, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(rightColumnX, 65, (pageWidth - (3 * margin)) / 2, 35, 2, 2, 'S');

        // Company Information Header
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.roundedRect(rightColumnX, 65, (pageWidth - (3 * margin)) / 2, 8, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text("NOTRE SOCIÉTÉ", rightColumnX + ((pageWidth - (3 * margin)) / 4), 70.5, { align: 'center' });

        // Company Information Content
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(companyData.name, rightColumnX + 5, 78);
        doc.setFont('helvetica', 'normal');
        doc.text(`Adresse: ${companyData.address}`, rightColumnX + 5, 83);
        doc.text(`Tél: ${companyData.phone1}`, rightColumnX + 5, 88);
        doc.text(`ICE: ${companyData.ice}`, rightColumnX + 5, 93);

        // Document Information - Add below the supplier and company info
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(margin, 105, pageWidth - (2 * margin), 20, 2, 2, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(margin, 105, pageWidth - (2 * margin), 20, 2, 2, 'S');

        // Document Information Header
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.roundedRect(margin, 105, pageWidth - (2 * margin), 8, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text("INFORMATION DOCUMENT", pageWidth / 2, 110.5, { align: 'center' });

        // Document Information Content
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');

        // Create two columns for document info
        const col1X = margin + 5;
        const col2X = pageWidth / 2 + 5;

        // Show internal number and supplier invoice number
        doc.text(`N° interne: ${invoice.numero}`, col1X, 118);
        doc.text(`N° facture fournisseur: ${invoice.numeroFactfournisseur || 'N/A'}`, col2X, 118);

        // Add date on the next line
        doc.text(`Date: ${formattedDate}`, col1X, 123);

        // Add reference to receipt numbers if available (next line)
        if (invoice.referenceBons) {
            doc.text(`Bons de réception: ${invoice.referenceBons}`, col1X, 128);
        }

        // Start table at a lower Y position
        const tableStartY = 130;

        // Add table for items with improved styling
        const tableData = invoice.lignes ? invoice.lignes.map((ligne) => {
            return [
                ligne.articleCode || '',
                ligne.articleNom || '',
                ligne.quantite || '',
                ligne.unite || 'U',
                `${formatPrice(ligne.prixUnitaireHT, false)}`,
                `${formatPrice(ligne.montantHT, false)}`,
            ];
        }) : [];

        // Determine if this is a small document (few lines)
        const isSmallDocument = !invoice.lignes || invoice.lignes.length <= 6;

        // Define table headers
        const tableHeaders = ["Code", "Description", "Qté", "UM", "PU HT", "Total HT"];

        // Add the table with automatic pagination
        autoTable(doc, {
            startY: tableStartY,
            head: [tableHeaders],
            body: tableData,
            styles: {
                fontSize: 9,
                cellPadding: 3,
            },
            headStyles: {
                fillColor: primaryColor,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'center',
            },
            columnStyles: {
                0: { halign: 'center', cellWidth: 25 },
                1: { halign: 'left', cellWidth: 70 },
                2: { halign: 'center', cellWidth: 15 },
                3: { halign: 'center', cellWidth: 20 },
                4: { halign: 'right', cellWidth: 30 },
                5: { halign: 'right', cellWidth: 30 },
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245],
            },
            margin: { top: tableStartY, right: margin, bottom: 30, left: margin },
            didDrawPage: (data) => {
                // Add header and footer on each page
                if (data.pageNumber > 1) {
                    addHeader(doc, false);
                }
                addFooter(doc);
            },
        });

        // Get the Y position after the table
        let currentY = doc.lastAutoTable.finalY + 5;

        // Calculate total values
        const totalHT = invoice.montantHT || 0;
        const totalTVA = invoice.montantTVA || 0;
        const totalTTC = invoice.montantTTC || 0;

        // Create a modern summary box
        const summaryHeight = isSmallDocument ? 60 : 70;
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(rightColumnX, currentY, (pageWidth - (3 * margin)) / 2, summaryHeight, 2, 2, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(rightColumnX, currentY, (pageWidth - (3 * margin)) / 2, summaryHeight, 2, 2, 'S');

        // Summary header
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.roundedRect(rightColumnX, currentY, (pageWidth - (3 * margin)) / 2, 10, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text("RÉCAPITULATIF", rightColumnX + ((pageWidth - (3 * margin)) / 4), currentY + 6, { align: 'center' });

        // Summary content
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');

        const summaryStartY = currentY + 15;
        const summaryLineHeight = 8;

        // Total HT
        doc.text("Total HT:", rightColumnX + 5, summaryStartY);
        doc.text(`${formatPrice(totalHT, false)} DH`, rightColumnX + (pageWidth - (3 * margin)) / 2 - 5, summaryStartY, { align: 'right' });

        // Total TVA
        doc.text("Total TVA:", rightColumnX + 5, summaryStartY + summaryLineHeight);
        doc.text(`${formatPrice(totalTVA, false)} DH`, rightColumnX + (pageWidth - (3 * margin)) / 2 - 5, summaryStartY + summaryLineHeight, { align: 'right' });

        // Separator line
        doc.setDrawColor(220, 220, 220);
        doc.line(
            rightColumnX + 5,
            summaryStartY + summaryLineHeight * 1.5,
            rightColumnX + (pageWidth - (3 * margin)) / 2 - 5,
            summaryStartY + summaryLineHeight * 1.5
        );

        // Total TTC with highlight
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text("Total TTC:", rightColumnX + 5, summaryStartY + summaryLineHeight * 2.5);
        doc.text(`${formatPrice(totalTTC, false)} DH`, rightColumnX + (pageWidth - (3 * margin)) / 2 - 5, summaryStartY + summaryLineHeight * 2.5, { align: 'right' });

        // Status information
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Statut: ${invoice.statut || 'N/A'}`, rightColumnX + 5, summaryStartY + summaryLineHeight * 4);

        // Add amount in words with better styling
        const totalInWords = convertAmountToWords(Math.round(totalTTC));
        const wordsBoxHeight = isSmallDocument ? 25 : 30;
        doc.setFillColor(245, 245, 245);
        doc.roundedRect(margin, currentY, (pageWidth - (3 * margin)) / 2, wordsBoxHeight, 2, 2, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(margin, currentY, (pageWidth - (3 * margin)) / 2, wordsBoxHeight, 2, 2, 'S');

        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text("Arrêté la présente facture fournisseur à la somme de:", margin + 5, currentY + 8);
        doc.setTextColor(60, 60, 60);
        doc.setFont('helvetica', 'italic');

        // Split long text into multiple lines if needed
        const maxWidth = (pageWidth - (3 * margin)) / 2 - 10;
        const splitText = doc.splitTextToSize(totalInWords, maxWidth);
        doc.text(splitText, margin + 5, currentY + 16);

        // Update current Y position after the totals section
        currentY += isSmallDocument ? 35 : 40;

        // Add payment information section
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(margin, currentY, pageWidth - (2 * margin), 30, 2, 2, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(margin, currentY, pageWidth - (2 * margin), 30, 2, 2, 'S');

        // Payment Information Header
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.roundedRect(margin, currentY, pageWidth - (2 * margin), 8, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text("INFORMATIONS DE PAIEMENT", pageWidth / 2, currentY + 5, { align: 'center' });

        // Payment Information Content
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');

        // Create two columns for payment info
        const paymentCol1X = margin + 5;
        const paymentCol2X = pageWidth / 2 + 5;
        const paymentStartY = currentY + 15;

        // Mode de paiement
        doc.text(`Mode de paiement: ${invoice.modePaiement || 'N/A'}`, paymentCol1X, paymentStartY);

        // Date d'échéance if available
        if (invoice.dateEcheance) {
            const formattedEcheance = new Date(invoice.dateEcheance).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            doc.text(`Date d'échéance: ${formattedEcheance}`, paymentCol2X, paymentStartY);
        }

        // Conditions de paiement if available
        if (invoice.conditionsPaiement) {
            doc.text(`Conditions: ${invoice.conditionsPaiement}`, paymentCol1X, paymentStartY + 8);
        }

        // Update current Y position after payment info
        currentY += 40;

        // Add terms and conditions
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.setFont('helvetica', 'normal');
        doc.text("Conditions et modalités:", margin, currentY);
        doc.setFontSize(8);

        // Reduced line spacing for small document
        const lineSpacing = isSmallDocument ? 5 : 7;

        // Set terms for supplier invoice
        doc.text("• Les prix indiqués sont en Dirhams (DH) et incluent la TVA.", margin, currentY + lineSpacing);
        doc.text("• Le paiement doit être effectué selon les conditions convenues.", margin, currentY + (lineSpacing * 2));
        doc.text("• En cas de litige, seuls les tribunaux de notre juridiction sont compétents.", margin, currentY + (lineSpacing * 3));

        // Check if we need to add a new page for signature
        const signatureHeight = isSmallDocument ? 35 : 40;

        // Only add a new page if absolutely necessary
        const remainingSpace = pageHeight - footerHeight - currentY;
        if (remainingSpace < signatureHeight) {
            doc.addPage();
            addHeader(doc, false);
            addFooter(doc);
            currentY = 30; // Reset Y position on new page
        }

        // Add signature section
        doc.setDrawColor(200, 200, 200);
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.setFont('helvetica', 'bold');
        doc.text("Signature et cachet:", margin, currentY + (lineSpacing * 5));

        // Draw signature box
        const signatureBoxHeight = isSmallDocument ? 20 : 25;
        doc.rect(margin, currentY + (lineSpacing * 6), 60, signatureBoxHeight, 'S');

        // Add footer to the first page
        addFooter(doc);

        // Save the PDF
        doc.save(`Facture-Fournisseur-${invoice.numero}.pdf`);
    };


    const clearFilters = () => {
        setFilters({
            query: '',
            fournisseurId: null,
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(tomorrowDate)
        });
    };


    const getStatusBadge = (status) => {
        switch (status) {
            case 'IMPAYE':
                return <Badge color="danger">Impayée</Badge>;
            case 'PAYEE':
                return <Badge color="success">Payée</Badge>;
            case 'PARTIELLEMENT_PAYEE':
                return <Badge color="warning">Partiellement payée</Badge>;
            case 'ANNULEE':
                return <Badge color="secondary">Annulée</Badge>;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                }}
            >
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaFileInvoice className="text-primary me-2" />
                                {t('facture.list')}
                            </h4>
                            {/* <Button color="primary" onClick={() => window.location.href = '/invoices/new'}>
                                <FaPlus className="me-2" /> {t('facture.newInvoice')}
                            </Button> */}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {/* Filtres et actions en lot */}
            <Card className="shadow-sm mb-4">
                <CardBody>
                    <Row className="mb-3 g-2 align-items-end">
                        <Col lg={7} md={4} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.search')}</Label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <Input
                                        type="text"
                                        innerRef={searchInputRef}
                                        placeholder={t('facture.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilters(prevFilters => ({
                                                ...prevFilters,
                                                query: value
                                            }));
                                            debouncedSearch(value);
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-3"
                                style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <table className="table table-hover table-striped table-bordered" style={{fontSize:12}}>
                                <thead className="thead-light">
                                    <tr>
                                        <th className="align-middle text-center">{t('facture.table.invoiceNb')}</th>
                                        <th className="align-middle text-center">{t('purchase.supplier')}</th>
                                        <th className="align-middle text-center">{t('facture.dateFacture')}</th>
                                        <th className="align-middle text-center">N° Fact Fourn.</th>
                                        <th className="align-middle text-center">{t('facture.dateOperation')}</th>
                                        <th className="align-middle text-center">{t('facture.table.totalTtc')}</th>
                                        <th className="align-middle text-center">{t('facture.table.totalHt')}</th>
                                        <th className="align-middle text-center">{t('reception.totalTva')}</th>
                                        <th className="align-middle text-center">{t('facture.montantRegle')}</th>
                                        <th className="align-middle text-center">{t('facture.table.receiptNumbers')}</th>
                                        <th className="align-middle text-center">{t('facture.table.status')}</th>
                                        <th className="align-middle text-center">{t('facture.table.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices?.length === 0 && (
                                        <tr>
                                            <td colSpan="12" className="text-center text-muted">
                                                {t('facture.noInvoices')}
                                            </td>
                                        </tr>
                                    )}
                                    {invoices?.map((invoice) => (
                                        <tr key={invoice.id}>
                                            <td className="align-middle text-center">{invoice.numero}</td>
                                            <td className="align-middle text-center">{invoice?.fournisseurNom}</td>
                                            <td className="align-middle text-center">{invoice.dateFacture}</td>
                                            <td className="align-middle text-center">{invoice.numeroFactfournisseur || 'N/A'}</td>
                                            <td className="align-middle text-center">{invoice.dateOperation}</td>
                                            <td className="align-middle text-center">{formatPrice(invoice.montantTTC, false)}</td>
                                            <td className="align-middle text-center">{formatPrice(invoice.montantHT, false)}</td>
                                            <td className="align-middle text-center">{formatPrice(invoice.montantTVA, false)}</td>
                                            <td className="align-middle text-center">{formatPrice(invoice.montantRegle, false)}</td>
                                            <td className="align-middle text-center">
                                                {invoice.referenceBons ? (
                                                    <div className="d-flex flex-column align-items-center">
                                                        {invoice.referenceBons.split(',').map((receipt, index) => (
                                                            <Badge key={index} color="success" className='mb-1' style={{ fontSize: '10px' }}>
                                                                {receipt.trim()}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">Aucun</span>
                                                )}
                                            </td>
                                            <td className="align-middle text-center">
                                                {getStatusBadge(invoice.statut)}
                                            </td>
                                            <td className="align-middle text-center">
                                                <Button color="secondary" title='Téléchargé PDF' onClick={() => handleGeneratePdf(invoice)} size="sm" className="me-1 px-2 py-1">
                                                    <FaFilePdf size={12} className="mr-2" />
                                                </Button>

                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light fw-bold">
                                        <td colSpan="5" className="text-end">{t('common.totals')}</td>
                                        <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantTTC, 0), false)}</td>
                                        <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantHT, 0), false)}</td>
                                        <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantTVA, 0), false)}</td>
                                        <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantRegle, 0), false)}</td>
                                        <td colSpan="2"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </>)}
        </Container>
    );
};

export default SupplierInvoiceList;